import { Box, Typography, TextField, Autocomplete, FormControl, Select, MenuItem, InputLabel, Alert } from "@mui/material";
import renderInputMatchAdornment from "./InputMatchAdornment";

const SUPPLIER_ID = process.env.REACT_APP_SUPPLIER_ID;

const limitDecimals = (value) => {
  if (value.includes(".")) {
    const [int, dec] = value.split(".");
    return int + "." + dec.slice(0, 2);
  }
  return value;
};

export default function SupplierCostSection({
  isEditing,
  cin7Product,
  shopifyProduct,
  values,
  setValues,
  suppliers,
  brands,
  error,
}) {
  const supplierMatches = values.supplier === SUPPLIER_ID;
  const shopifyUnitPerCase = shopifyProduct?.metafields?.find?.((m) => m.key === "units_per_case")?.value;

  const providerValue = ["AGLC-Allocated", "AGLC-DoNotUpdate"].includes(values.metafields?.provider)
    ? values.metafields?.provider
    : "";

  const prevLatestCost = Number(cin7Product?.latestPrice);
  const prevFixedCost = Number(cin7Product?.fixedPrice);

  const hasLatestChangedToZero =
    isEditing &&
    prevLatestCost > 0 &&
    values.latestPrice !== "" &&
    Number(values.latestPrice) === 0;

  const hasFixedChangedToZero =
    isEditing &&
    prevFixedCost > 0 &&
    values.fixedPrice !== "" &&
    Number(values.fixedPrice) === 0;

  const showZeroCostInfo = hasLatestChangedToZero || hasFixedChangedToZero;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Supplier & Cost
      </Typography>

      {/* ROW 1 — Supplier / Latest Price / Fixed Price */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isEditing ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr",
          gap: 2,
          mb: 2,
        }}
      >
        <Autocomplete
          options={suppliers}
          value={suppliers.find(s => s.value === values.supplier) || null}
          onChange={(e, newValue) => {
            const newSupplier = newValue?.value || "";
            setValues(prev => ({
              ...prev,
              supplier: newSupplier,
              metafields: {
                ...prev.metafields,
                provider:
                  newSupplier === SUPPLIER_ID ? prev.metafields?.provider : "",
              },
            }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Supplier"
              required
              fullWidth
              size="small"
              error={error && !values.supplier}
              helperText="Required field"
            />
          )}
        />

        <TextField
          label="Cost (Latest)"
          name="latestPrice"
          type="number"
          required
          value={values.latestPrice}
          onChange={(e) =>
            setValues(prev => ({
              ...prev,
              latestPrice: limitDecimals(e.target.value),
            }))
          }
          error={error && values.latestPrice === ""}
          helperText="Required field"
          fullWidth
          size="small"
        />

        <TextField
          label="Cost (Fixed)"
          name="fixedPrice"
          type="number"
          required
          value={values.fixedPrice}
          onChange={(e) =>
            setValues(prev => ({
              ...prev,
              fixedPrice: limitDecimals(e.target.value),
            }))
          }
          error={error && values.fixedPrice === ""}
          helperText="Required field"
          fullWidth
          size="small"
        />
        {isEditing && (
          <TextField
            label="Average Cost"
            name="averageCost"
            type="number"
            fullWidth
            size="small"
            value={values.averageCost || ""}
            disabled
          />
        )}
      </Box>

      {/* ROW 2 — AGLC Allocation */}
      <Box sx={{ mb: 2 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="additional-supplier-info-label">Additional Supplier Information</InputLabel>
          <Select
            labelId="additional-supplier-info-label"
            id="additional-supplier-info-select"
            value={providerValue}
            label="Additional Supplier Information"
            onChange={(event) => {
              setValues(prev => ({
                  ...prev,
                  metafields: {
                    ...prev.metafields,
                    provider: event.target.value,
                  },
                }))
            }}
            disabled={!supplierMatches}
          >
            <MenuItem value="">&nbsp;</MenuItem>
            <MenuItem value="AGLC-Allocated">This product is allocated to ZYN in AGLC (AGLC-Allocated)</MenuItem>
            <MenuItem value="AGLC-DoNotUpdate">Skip this product in Continue Selling updates (AGLC-DoNotUpdate)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* ROW 3 — Brand / Units Per Case */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
        }}
      >
        <Autocomplete
          options={brands}
          value={brands.find(b => b.value === values.brand) || null}
          onChange={(e, newValue) =>
            setValues(prev => ({
              ...prev,
              brand: newValue?.value || "",
            }))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Sales Rep"
              slotProps={{
                input: {
                  ...params.InputProps,
                  ...renderInputMatchAdornment(
                    isEditing,
                    cin7Product?.brand,
                    shopifyProduct?.vendor
                  ),
                }
              }}
              required
              fullWidth
              size="small"
              error={error && !values.brand}
              helperText="Required field"
            />
          )}
        />

        <TextField
          label="Units per case"
          name="unitsPerCase"
          type="number"
          required
          value={values.unitsPerCase}
          onChange={(e) =>
            setValues(prev => ({
              ...prev,
              unitsPerCase: e.target.value,
              metafields: {
                ...prev.metafields,
                units_per_case: e.target.value,
              },
            }))
          }
          error={error && !values.unitsPerCase}
          helperText="Required field"
          fullWidth
          size="small"
          slotProps={{
            input: renderInputMatchAdornment(isEditing, cin7Product?.supplierInventoryCode, shopifyUnitPerCase)
          }}
        />
      </Box>

      {showZeroCostInfo && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>FYI:</strong> Cin7 does not update the existing value in latest cost and/or fixed cost if the new cost is 0.
            The information will be sent to Cin7 and it will accept it, but it will not update the pre-existing cost.
          </Typography>
        </Alert>
      )}
    </Box>
  );
}